import React, { useState, useEffect } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import axios from 'axios';
import { useAuth } from '../App';
import {
  LayoutDashboard,
  FileText,
  FolderKanban,
  Users,
  Settings,
  LogOut,
  ChevronDown,
  ChevronRight,
  Menu,
  X,
  ClipboardList,
  DollarSign,
  Building2,
  ShoppingCart,
  Factory,
  Wrench,
  Package,
  HardHat,
  Shield,
  Truck,
  Globe,
  Mail
} from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const Sidebar = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const { user, logout } = useAuth();
  const [expandedItems, setExpandedItems] = useState({});
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [pendingDispatch, setPendingDispatch] = useState(0);
  const [portalPending, setPortalPending] = useState(0);
  const [timeOffPending, setTimeOffPending] = useState(0);

  // Time Off: requests waiting on HR review / manager approval
  useEffect(() => {
    const TO_ROLES = ['super_admin', 'admin', 'office_admin', 'hr', 'project_manager', 'project_manager_estimator', 'fabrication_manager'];
    if (!user || !(TO_ROLES.includes(user.role) || (user.extra_permissions || []).includes('hr_management'))) return;
    let active = true;
    const fetchCount = async () => {
      try {
        const res = await axios.get(`${BACKEND_URL}/api/time-off/requests`, { params: { status: 'submitted,hr_reviewed' }, headers: { Authorization: `Bearer ${localStorage.getItem('token')}` } });
        if (active) setTimeOffPending((res.data?.requests || []).length);
      } catch (e) { /* silent */ }
    };
    fetchCount();
    const t = setInterval(fetchCount, 60000);
    return () => { active = false; clearInterval(t); };
  }, [user]);

  // Customer Portal: unread customer messages + new service requests + photos awaiting approval
  useEffect(() => {
    const PORTAL_ROLES = ['super_admin', 'admin', 'office_admin', 'project_manager', 'project_manager_estimator', 'dispatch'];
    if (!user || !(PORTAL_ROLES.includes(user.role) || (user.extra_permissions || []).includes('customer_portal'))) return;
    let active = true;
    const fetchCount = async () => {
      try {
        const res = await axios.get(`${BACKEND_URL}/api/portal-admin/unread-count`, { headers: { Authorization: `Bearer ${localStorage.getItem('token')}` } });
        if (active) setPortalPending(res.data?.total || 0);
      } catch (e) { /* silent */ }
    };
    fetchCount();
    const t = setInterval(fetchCount, 60000);
    return () => { active = false; clearInterval(t); };
  }, [user]);

  // Live pending-dispatch-request badge for approvers (dispatch/admins)
  useEffect(() => {
    const APPROVERS = ['super_admin', 'admin', 'office_admin', 'dispatch'];
    if (!user || !APPROVERS.includes(user.role)) return;
    let active = true;
    const fetchCount = async () => {
      try {
        const res = await axios.get(`${BACKEND_URL}/api/dispatch/requests/pending-count`, {
          headers: { Authorization: `Bearer ${localStorage.getItem('token')}` },
        });
        if (active) setPendingDispatch(res.data?.count || 0);
      } catch (e) { /* silent */ }
    };
    fetchCount();
    const t = setInterval(fetchCount, 60000);
    return () => { active = false; clearInterval(t); };
  }, [user]);

  const toggleExpand = (itemName) => {
    setExpandedItems(prev => ({
      ...prev,
      [itemName]: !prev[itemName]
    }));
  };

  // Keep the group holding the current page open (e.g. landing on /shop-tracker opens "Fabrication").
  useEffect(() => {
    const p = location.pathname;
    const open = {};
    if (p === '/shop-tracker' || p.startsWith('/fab-scheduler')) open.Fabrication = true;
    if (['/field-tracker', '/dispatch-chat', '/schedule', '/photo-upload', '/tool-crib', '/skills'].includes(p) || p.startsWith('/expenses')) open.Millwright = true;
    if (['/team', '/hr/reviews', '/admin/password-management', '/hr/admin', '/admin/merge-accounts', '/time-off'].includes(p)) open['HR Management'] = true;
    if (p.startsWith('/mobile/') || ['/my-certificates', '/my-paperwork', '/safety-tickets'].includes(p)) open['Health & Safety'] = true;
    if (Object.keys(open).length) setExpandedItems(prev => ({ ...prev, ...open }));
  }, [location.pathname]);

  const handleLogout = async () => {
    await logout();
    navigate('/login');
  };

  // Role groups for access control
  const MANAGEMENT = ['super_admin', 'office_admin', 'project_manager_estimator', 'fabrication_manager', 'dispatch'];
  const FOREMAN = ['shop_foreman', 'millwright_foreman'];
  const WORKERS = ['millwright', 'welder', 'labourer', 'tool_crib'];
  // Who can see the org-wide Certificates tracker (everyone's certs)
  const CERT_TRACKER_ROLES = ['super_admin', 'admin', 'office_admin', 'fabrication_manager', 'dispatch', 'shop_foreman', 'millwright_foreman'];

  // Visibility helpers (role list, or a per-user permission grant)
  const hasPerm = (key) => !!key && (user?.extra_permissions || []).includes(key);
  const canSee = (s) => !s.roles || s.roles.includes(user?.role) || hasPerm(s.permKey);
  // Nested groups (e.g. Admin → HR Management → …) only show when at least one leaf is visible
  const visibleChildren = (item) => (item.subItems || []).filter(canSee).filter((s) => !s.subItems || visibleChildren(s).length > 0);
  const badgeOf = (item) => (item.subItems ? visibleChildren(item).reduce((n, s) => n + badgeOf(s), 0) : (item.badge > 0 ? item.badge : 0));

  const SAFETY_ACTIVE = location.pathname.startsWith('/mobile/') || ['/hr/dashboard', '/my-certificates', '/my-paperwork', '/safety-tickets'].includes(location.pathname) || location.pathname.startsWith('/hr/employee/');
  const safetySection = {
    name: 'Health & Safety',
    icon: HardHat,
    active: SAFETY_ACTIVE,
    roles: null, // Section is visible to everyone; sub-items are gated individually below
    subItems: [
      { name: 'Inspections', path: '/mobile/inspections' },
      { name: 'Employee Certifications', path: '/safety-tickets', permKey: 'safety_tickets', roles: ['super_admin', 'admin', 'office_admin', 'hr', 'fabrication_manager', 'dispatch', 'shop_foreman', 'millwright_foreman', 'foreman', 'project_manager', 'project_manager_estimator', 'estimator'] },
      { name: 'My Certificates', path: '/my-certificates' },
      { name: 'My Paperwork', path: '/my-paperwork' }
    ]
  };
  const HR_ROLES = ['super_admin', 'office_admin'];
  const HR_ACTIVE = ['/team', '/hr/reviews', '/admin/password-management', '/hr/admin', '/admin/merge-accounts', '/time-off'].includes(location.pathname);
  const hrSection = {
    name: 'HR Management',
    icon: Shield,
    active: HR_ACTIVE,
    roles: null, // Time Off is for everyone; the HR tools are gated individually
    subItems: [
      { name: 'Time Off', path: '/time-off', badge: timeOffPending, badgeTestId: 'time-off-nav-badge' },
      { name: 'User Management', path: '/team', permKey: 'hr_management', roles: HR_ROLES },
      { name: 'Review Tracker', path: '/hr/reviews', permKey: 'hr_management', roles: HR_ROLES },
      { name: 'Onboarding Console', path: '/hr/admin', permKey: 'hr_management', roles: HR_ROLES },
      { name: 'Password Reset', path: '/admin/password-management', permKey: 'hr_management', roles: HR_ROLES },
      { name: 'Merge Accounts', path: '/admin/merge-accounts', roles: ['super_admin', 'admin'] }
    ]
  };
  const adminSections = [
    {
      name: 'Admin Panel',
      roles: ['super_admin'],
      subItems: [
        { name: 'System Console', path: '/admin/console' },
        { name: 'Program Updates', path: '/admin/changelog' },
        { name: 'Pay Rates', path: '/admin/pay-rates' },
      ]
    },
    {
      name: 'Fleet Maintenance',
      path: '/fleet-maintenance',
      roles: ['super_admin', 'admin', 'office_admin', 'dispatch', 'shop_foreman', 'fabrication_manager', 'project_manager', 'project_manager_estimator', 'estimator', 'millwright_foreman', 'foreman']
    },
    {
      name: 'CRM',
      permKey: 'crm',
      roles: ['super_admin', 'office_admin', 'project_manager_estimator'],
      subItems: [
        { name: 'Pipeline', path: '/crm/pipeline' },
        { name: 'Contacts', path: '/crm/contacts' },
        { name: 'Ratesheets', path: '/crm/ratesheets' },
        { name: 'Dashboard', path: '/crm' }
      ]
    }
  ];
  // Office/admin roles get Safety & Training tucked under Admin; field & shop staff (incl. foremen) keep it top-level.
  const ADMIN_HEADING_ROLES = ['super_admin', 'admin', 'office_admin', 'hr', 'dispatch', 'fabrication_manager', 'project_manager', 'project_manager_estimator', 'estimator'];
  const isAdminUser = ADMIN_HEADING_ROLES.includes(user?.role);
  const ADMIN_ICONS = { 'Admin Panel': Settings, 'HR Management': Shield, 'Fleet Maintenance': Truck, 'CRM': Users };

  const allNavItems = [
    {
      name: 'Dashboard',
      path: '/dashboard',
      icon: LayoutDashboard,
      active: location.pathname === '/dashboard',
      roles: null // Everyone
    },
    {
      name: 'Mail',
      path: '/mail',
      icon: Mail,
      active: location.pathname === '/mail',
      roles: ['super_admin', 'admin', 'office_admin', 'project_manager', 'project_manager_estimator', 'dispatch', 'fabrication_manager']
    },
    {
      name: 'Fabrication',
      icon: Factory,
      active: location.pathname === '/shop-tracker' || location.pathname.startsWith('/fab-scheduler') || location.pathname.startsWith('/packing-slips'),
      roles: null, // Shop Tracker is for everyone; sub-items gated individually
      subItems: [
        { name: 'Shop Tracker', path: '/shop-tracker' },
        { name: 'Fabrication Scheduler', path: '/fab-scheduler', permKey: 'fab_scheduler', roles: ['super_admin', 'admin', 'fabrication_manager', 'shop_foreman', 'project_manager', 'project_manager_estimator', 'estimator', 'office_admin'] },
        { name: 'Packing Slips', path: '/packing-slips', permKey: 'packing_slips', roles: ['welder', 'fitter', 'shop_foreman', 'fabrication_manager', 'super_admin', 'office_admin'] },
      ]
    },
    {
      name: 'Millwright',
      icon: Wrench,
      active: ['/field-tracker', '/dispatch-chat', '/schedule', '/photo-upload', '/tool-crib', '/manpower', '/dailies', '/dispatch-requests', '/skills'].includes(location.pathname) || location.pathname.startsWith('/expenses'),
      roles: null, // Dispatch Chat + Upload Photos are for everyone; sub-items gated individually
      subItems: [
        { name: 'Field Tracker', path: '/field-tracker', permKey: 'field_tracker', roles: ['super_admin', 'office_admin', 'project_manager_estimator', 'dispatch', 'millwright_foreman', 'millwright', 'welder', 'labourer', 'tool_crib'] },
        { name: 'Dispatch Chat', path: '/dispatch-chat' },
        { name: 'Millwright Schedule', path: '/schedule' },
        { name: 'Dispatch Requests', path: '/dispatch-requests', permKey: 'dispatch_requests', roles: ['super_admin', 'admin', 'office_admin', 'project_manager', 'project_manager_estimator', 'dispatch'], badge: pendingDispatch, badgeTestId: 'dispatch-pending-badge' },
        { name: 'Tool Crib', path: '/tool-crib', permKey: 'tool_crib', roles: ['super_admin', 'admin', 'office_admin', 'dispatch', 'shop_foreman', 'tool_crib', 'project_manager', 'project_manager_estimator', 'millwright_foreman', 'foreman'] },
        { name: 'Skill Profiles', path: '/skills', roles: ['super_admin', 'admin', 'office_admin', 'dispatch', 'project_manager', 'project_manager_estimator', 'millwright_foreman', 'shop_foreman', 'foreman', 'fabrication_manager', 'millwright', 'welder', 'labourer', 'apprentice', 'fitter'] },
        { name: 'Expenses', path: '/expenses', permKey: 'expenses', roles: ['super_admin', 'admin', 'office_admin', 'project_manager', 'project_manager_estimator', 'estimator', 'foreman', 'shop_foreman', 'millwright_foreman', 'fabrication_manager', 'millwright'] },
        { name: 'Upload Photos', path: '/photo-upload' },
      ]
    },
    {
      name: 'Material Sheets',
      path: '/material-sheets',
      icon: ClipboardList,
      active: location.pathname === '/material-sheets',
      roles: null // Everyone — anyone can build a costed pick list for a job
    },
    {
      name: 'Company Hub',
      path: '/company',
      icon: Users,
      active: location.pathname === '/company',
      roles: null // Everyone
    },
    {
      name: 'Consumables',
      path: '/catalog',
      icon: Package,
      active: location.pathname === '/catalog',
      permKey: 'consumables',
      roles: ['super_admin', 'office_admin', 'project_manager_estimator', 'fabrication_manager', 'shop_foreman', 'dispatch']
    },
    {
      name: 'Quotes',
      path: '/quotes',
      icon: FileText,
      active: location.pathname.startsWith('/quotes'),
      permKey: 'quotes',
      roles: ['super_admin', 'office_admin', 'project_manager_estimator', 'fabrication_manager']
    },
    {
      name: 'Projects',
      path: '/projects',
      icon: FolderKanban,
      active: location.pathname.startsWith('/projects'),
      permKey: 'projects',
      roles: ['super_admin', 'office_admin', 'project_manager_estimator', 'fabrication_manager']
    },
    {
      name: 'Purchase Orders',
      path: '/purchase-orders',
      icon: ShoppingCart,
      active: location.pathname.startsWith('/purchase-orders'),
      permKey: 'purchase_orders',
      roles: ['super_admin', 'admin', 'office_admin', 'project_manager', 'project_manager_estimator', 'dispatch', 'fabrication_manager', 'shop_foreman']
    },
    {
      name: 'Customer Portal',
      path: '/portal-admin',
      icon: Globe,
      active: location.pathname === '/portal-admin',
      permKey: 'customer_portal',
      roles: ['super_admin', 'admin', 'office_admin', 'project_manager', 'project_manager_estimator', 'dispatch'],
      badge: portalPending
    },
    ...(isAdminUser ? [] : adminSections.map((s) => ({ ...s, icon: ADMIN_ICONS[s.name], active: location.pathname === s.path || visibleChildren(s).some((k) => location.pathname === k.path || location.pathname.startsWith(k.path + '/')) }))),
    hrSection,
    safetySection,
    {
      name: 'Admin',
      icon: Settings,
      active: (location.pathname.startsWith('/admin') && !HR_ACTIVE) || location.pathname === '/fleet-maintenance' || location.pathname.startsWith('/crm'),
      roles: ADMIN_HEADING_ROLES,
      subItems: adminSections
    }
  ];

  // Filter navigation items based on user role + per-user permission grants
  const navItems = allNavItems.filter((item) => canSee(item) && (!item.subItems || visibleChildren(item).length > 0));

  const badgePill = (n, testId, extra = 'ml-2') => n > 0 && (
    <span className={`${extra} bg-red-500 text-white text-xs font-bold rounded-full px-2 py-0.5 min-w-[20px] text-center`} data-testid={testId}>{n}</span>
  );

  // Renders a sub-item: a link, or a collapsible nested group
  const renderSub = (subItem, siblings, depth = 0) => {
    const key = subItem.name;
    if (subItem.subItems) {
      const kids = visibleChildren(subItem);
      const open = expandedItems[key];
      const groupActive = kids.some((k) => location.pathname === k.path || location.pathname.startsWith(k.path + '/'));
      return (
        <div key={key}>
          <button
            type="button"
            onClick={() => toggleExpand(key)}
            data-testid={`nav-group-${key.replace(/\s+/g, '-').toLowerCase()}`}
            className={`w-full flex items-center px-3 py-2 rounded-lg text-sm transition-all duration-200 ${groupActive ? 'text-white font-semibold' : 'text-slate-300 hover:bg-white/5 hover:text-white'}`}
          >
            <span className="flex-1 text-left">{subItem.name}</span>
            {!open && badgePill(badgeOf(subItem), `nav-group-badge-${key.replace(/\s+/g, '-').toLowerCase()}`, 'mr-2')}
            {open ? <ChevronDown className="h-3.5 w-3.5" /> : <ChevronRight className="h-3.5 w-3.5" />}
          </button>
          {open && <div className="ml-4 mt-0.5 space-y-0.5 border-l border-white/15 pl-2">{kids.map((k) => renderSub(k, kids, depth + 1))}</div>}
        </div>
      );
    }
    const exact = siblings.find((s) => location.pathname === s.path);
    const subActive = exact ? exact.path === subItem.path : location.pathname.startsWith(subItem.path + '/');
    return (
      <Link
        key={key}
        to={subItem.path}
        data-testid={`nav-sub-${subItem.path.replace(/^\//, '').replace(/\//g, '-')}`}
        onClick={() => setMobileMenuOpen(false)}
        className={`flex items-center px-3 py-2 rounded-lg text-sm transition-all duration-200 ${
          subActive ? 'bg-sky-400/15 text-white font-semibold ring-1 ring-sky-400/30' : 'text-slate-300 hover:bg-white/5 hover:text-white'
        }`}
      >
        <span className="flex-1">{subItem.name}</span>
        {badgePill(subItem.badge, subItem.badgeTestId || `nav-badge-${subItem.path.slice(1)}`)}
      </Link>
    );
  };

  return (
    <>
      {/* Mobile Menu Button - Fixed at top left */}
      <button
        onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
        data-testid="mobile-menu-button"
        className="lg:hidden fixed top-3 left-3 z-50 bg-[#0C1526] hover:bg-slate-800 text-white p-2.5 rounded-xl shadow-lg transition-all duration-300"
      >
        {mobileMenuOpen ? <X className="h-5 w-5" /> : <Menu className="h-5 w-5" />}
      </button>

      {/* Overlay */}
      {mobileMenuOpen && (
        <div
          className="lg:hidden fixed inset-0 bg-black bg-opacity-50 z-40"
          onClick={() => setMobileMenuOpen(false)}
        />
      )}

      {/* Sidebar */}
      <div data-shell="sidebar" className={`
        fixed lg:static
        inset-y-0 left-0
        z-40 lg:z-0
        w-64 h-screen flex flex-col
        bg-[#0C1526] border-r border-white/5 shadow-[8px_0_30px_rgba(12,21,38,0.12)]
        transition-transform duration-300 ease-in-out
        ${mobileMenuOpen ? 'translate-x-0' : '-translate-x-full lg:translate-x-0'}
      `}>
        {/* Logo/Brand */}
        <div className="flex-shrink-0 px-4 py-4 border-b border-white/10">
          <div className="flex items-center justify-center space-x-1">
            <img 
              src="/365ops-logo.png" 
              alt="365 Industrial Solutions" 
              className="h-28 w-28"
              style={{marginRight: '-30px', objectFit: 'contain'}}
            />
            <div>
              <h1 className="text-white font-bold text-2xl">365Ops</h1>
              <p className="text-slate-400 text-xs tracking-wide">Operations Management</p>
            </div>
          </div>
        </div>

        {/* User Info */}
        {user && (
          <div className="px-6 py-4 border-b border-white/10">
            <div className="text-white text-sm">
              <p className="text-[10px] font-bold uppercase tracking-[0.18em] text-slate-500">Signed in as</p>
              <p className="text-slate-100 font-semibold truncate mt-0.5">{user.full_name}</p>
              <p className="text-sky-300/90 text-xs capitalize mt-0.5">{user.role.replace('_', ' ')}</p>
            </div>
          </div>
        )}

        {/* Navigation */}
        <nav className="flex-1 overflow-y-auto px-3 py-4">
          <div className="space-y-1">
            {navItems.map((item) => {
              const Icon = item.icon;
              const isExpanded = expandedItems[item.name];
              const visibleSubItems = visibleChildren(item);
              const hasSubItems = visibleSubItems.length > 0;
              const groupBadge = badgeOf(item);
              
              if (item.disabled) {
                return (
                  <div
                    key={item.name}
                    className="flex items-center px-3 py-3 rounded-lg text-slate-400 cursor-not-allowed opacity-50"
                  >
                    {Icon && <Icon className="h-5 w-5 mr-3" />}
                    <span className="flex-1 text-sm font-medium">{item.name}</span>
                    <span className="text-xs bg-white/10 px-2 py-1 rounded">Soon</span>
                  </div>
                );
              }
              
              if (hasSubItems) {
                return (
                  <div key={item.name}>
                    <button
                      onClick={() => toggleExpand(item.name)}
                      data-testid={`nav-group-${item.name.replace(/\s+/g, '-').toLowerCase()}`}
                      className={`w-full flex items-center px-3 py-3 rounded-lg transition-all duration-200 ${
                        item.active
                          ? 'bg-sky-400/15 text-white ring-1 ring-sky-400/30 shadow-[inset_3px_0_0_0_#38bdf8]'
                          : 'text-slate-300 hover:bg-white/5 hover:text-white'
                      }`}
                    >
                      {Icon && <Icon className="h-5 w-5 mr-3" />}
                      <span className="flex-1 text-left text-sm font-medium">{item.name}</span>
                      {!isExpanded && badgePill(groupBadge, `nav-group-badge-${item.name.replace(/\s+/g, '-').toLowerCase()}`, 'mr-2')}
                      {isExpanded ? (
                        <ChevronDown className="h-4 w-4" />
                      ) : (
                        <ChevronRight className="h-4 w-4" />
                      )}
                    </button>
                  
                  {isExpanded && (
                    <div className="ml-6 mt-1 space-y-0.5 border-l border-white/10 pl-2">
                      {visibleSubItems.map((subItem) => renderSub(subItem, visibleSubItems))}
                    </div>
                  )}
                </div>
              );
            }
            
            return (
              <Link
                key={item.name}
                to={item.path}
                onClick={() => setMobileMenuOpen(false)}
                className={`flex items-center px-3 py-3 rounded-lg transition-all duration-200 ${
                  item.active
                    ? 'bg-sky-400/15 text-white ring-1 ring-sky-400/30 shadow-[inset_3px_0_0_0_#38bdf8]'
                    : 'text-slate-300 hover:bg-white/5 hover:text-white'
                }`}
              >
                {Icon && <Icon className="h-5 w-5 mr-3" />}
                <span className="flex-1 text-sm font-medium">{item.name}</span>
                {item.badge > 0 && (
                  <span
                    className="ml-2 bg-red-500 text-white text-xs font-bold rounded-full px-2 py-0.5 min-w-[20px] text-center"
                    data-testid={item.path === '/dispatch-requests' ? 'dispatch-pending-badge' : `nav-badge-${item.path.slice(1)}`}
                  >
                    {item.badge}
                  </span>
                )}
              </Link>
            );
          })}
        </div>
      </nav>

      {/* Logout */}
      <div className="px-3 py-4 border-t border-white/10">
        <button
          onClick={handleLogout}
          className="w-full flex items-center px-3 py-3 rounded-lg text-slate-300 hover:bg-red-500/20 hover:text-white transition-all duration-200"
        >
          <LogOut className="h-5 w-5 mr-3" />
          <span className="text-sm font-medium">Sign Out</span>
        </button>
      </div>
    </div>
    </>
  );
};

export default Sidebar;